import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import NieuweWerkbonModal from "../NieuweWerkbonModal";
import { useUser } from "../../context/UserContext";
import PlannerTimeRequestPanel from "../../components/time-requests/PlannerTimeRequestPanel";

export default function PLDashboard() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [monteurs, setMonteurs] = useState([]);
  const [allWerkbonnen, setAllWerkbonnen] = useState([]);
  const [allOpnames, setAllOpnames] = useState([]);
  const [attentionThreads, setAttentionThreads] = useState([]);
  const [pendingTimeApprovals, setPendingTimeApprovals] = useState([]);
  const [pendingTimeRequests, setPendingTimeRequests] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState("");
  const [approvalsError, setApprovalsError] = useState("");
  const [timeRequestsError, setTimeRequestsError] = useState("");
  const [hoursOverviewError, setHoursOverviewError] = useState("");
  const [timeRequestActionId, setTimeRequestActionId] = useState("");
  const [showNieuweWerkbon, setShowNieuweWerkbon] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const [periodFilter, setPeriodFilter] = useState("month");
  const [selectedProjectFilter, setSelectedProjectFilter] = useState("all");
  const [selectedHoursRowKey, setSelectedHoursRowKey] = useState("");
  const [cardDensity, setCardDensity] = useState(() => {
    if (typeof window === "undefined") return "compact";
    const saved = String(localStorage.getItem("pl.dashboard.cardDensity") || "").trim();
    return saved === "comfort" ? "comfort" : "compact";
  });
  const [actionPolicy, setActionPolicy] = useState({
    allowMonteurCreate: true,
    allowMonteurEditOwn: true,
    allowMonteurCloseOwn: true,
  });
  const [integrationSettings, setIntegrationSettings] = useState({
    moneybird: {
      enabled: false,
      connected: false,
      autoSyncOnInvoice: true,
    },
    tu: {
      enabled: false,
      connected: false,
      hasPassword: false,
    },
  });
  const [completionRequirements, setCompletionRequirements] = useState({
    requireExecutionLog: true,
    requireHours: true,
    requireMaterials: false,
    requireEvidence: true,
  });
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policySavingKey, setPolicySavingKey] = useState("");
  const [policyError, setPolicyError] = useState("");
  const [policySuccess, setPolicySuccess] = useState("");

  useEffect(() => {
    loadProjectData();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem("pl.dashboard.cardDensity", cardDensity);
  }, [cardDensity]);

  const isTenantAdmin = useMemo(() => {
    return getRoleNames(user).includes("tenantadmin");
  }, [user]);

  useEffect(() => {
    if (!isTenantAdmin) return;
    loadActionPolicies();
  }, [isTenantAdmin]);

  const technicianProjectHours = useMemo(
    () => buildTechnicianProjectHours(allWerkbonnen, projects, monteurs, periodFilter),
    [allWerkbonnen, projects, monteurs, periodFilter]
  );

  const projectFilterOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    for (const row of technicianProjectHours) {
      if (seen.has(row.projectKey)) continue;
      seen.add(row.projectKey);
      options.push({
        value: row.projectKey,
        label: row.projectLabel,
      });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label, "nl-NL"));
  }, [technicianProjectHours]);

  const filteredTechnicianProjectHours = useMemo(() => {
    if (selectedProjectFilter === "all") return technicianProjectHours;
    return technicianProjectHours.filter((row) => row.projectKey === selectedProjectFilter);
  }, [technicianProjectHours, selectedProjectFilter]);

  const selectedHoursRow = useMemo(() => {
    if (!selectedHoursRowKey) return null;
    return (
      filteredTechnicianProjectHours.find(
        (row) => getHoursRowKey(row) === selectedHoursRowKey
      ) || null
    );
  }, [filteredTechnicianProjectHours, selectedHoursRowKey]);

  const relatedWorkOrders = useMemo(() => {
    if (!selectedHoursRow) return [];
    const wanted = new Set(selectedHoursRow.workOrderIds || []);
    return (allWerkbonnen || [])
      .filter((wb) => wanted.has(String(wb?._id)))
      .sort((a, b) => {
        const dateA = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        const dateB = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        return dateB - dateA;
      });
  }, [allWerkbonnen, selectedHoursRow]);

  const actionRequiredItems = useMemo(
    () =>
      buildActionRequiredItems(
        allWerkbonnen,
        projects,
        clients,
        monteurs,
        completionRequirements
      ),
    [allWerkbonnen, projects, clients, monteurs, completionRequirements]
  );

  const actionRequiredSummary = useMemo(
    () => summarizeActionRequired(actionRequiredItems),
    [actionRequiredItems]
  );

  const communicationAttentionByProjectId = useMemo(() => {
    const counts = {};
    for (const thread of attentionThreads) {
      const projectId = String(thread?.projectId?._id || thread?.projectId || "").trim();
      if (!projectId) continue;
      counts[projectId] = Number(counts[projectId] || 0) + 1;
    }
    return counts;
  }, [attentionThreads]);

  const projectsWithCustomerReplies = useMemo(() => {
    return Object.values(communicationAttentionByProjectId).filter((count) => count > 0).length;
  }, [communicationAttentionByProjectId]);

  const opnameSummary = useMemo(
    () => summarizeOpnames(allOpnames, allWerkbonnen),
    [allOpnames, allWerkbonnen]
  );

  const openWorkordersCount = useMemo(() => {
    return (allWerkbonnen || []).filter((wb) => {
      const statusKey = normalizeWerkbonStatus(wb?.status);
      return !["afgerond", "gefactureerd", "gesloten", "cancelled", "annulled"].includes(statusKey);
    }).length;
  }, [allWerkbonnen]);

  const hoursThisMonth = useMemo(() => {
    const monthRows = buildTechnicianProjectHours(allWerkbonnen, projects, monteurs, "month");
    const total = monthRows.reduce((sum, row) => sum + Number(row?.totalHours || 0), 0);
    return Number.isFinite(total) ? Math.round(total * 10) / 10 : 0;
  }, [allWerkbonnen, projects, monteurs]);

  useEffect(() => {
    if (selectedProjectFilter === "all") return;
    if (!technicianProjectHours.some((row) => row.projectKey === selectedProjectFilter)) {
      setSelectedProjectFilter("all");
    }
  }, [selectedProjectFilter, technicianProjectHours]);

  useEffect(() => {
    if (!selectedHoursRowKey) return;
    if (!filteredTechnicianProjectHours.some((row) => getHoursRowKey(row) === selectedHoursRowKey)) {
      setSelectedHoursRowKey("");
    }
  }, [filteredTechnicianProjectHours, selectedHoursRowKey]);

  async function loadProjectData() {
    try {
      setLoadingProjects(true);
      setProjectsError("");
      setApprovalsError("");
      setTimeRequestsError("");
      setHoursOverviewError("");

      const [projectRes, clientRes, werkbonRes, monteurRes, settingsRes, timeRequestRes, communicationRes] = await Promise.all([
        api.get("/projects"),
        api.get("/clients"),
        api.get("/werkbonnen"),
        api.get("/users?role=Monteur"),
        api.get("/tenant/settings"),
        api.get("/time-extension-requests/pending"),
        api.get("/communications/threads", {
          params: {
            context: "project",
            needsInternalAttention: true,
          },
        }),
      ]);

      const projectList = Array.isArray(projectRes.data) ? projectRes.data : [];
      const clientList = Array.isArray(clientRes.data) ? clientRes.data : [];
      const werkbonnen = Array.isArray(werkbonRes.data) ? werkbonRes.data : [];
      const monteurList = asArray(monteurRes.data);

      let opnames = [];
      try {
        const opnameRes = await api.get("/opnames");
        opnames = Array.isArray(opnameRes.data) ? opnameRes.data : [];
      } catch {
        opnames = [];
      }

      setProjects(projectList);
      setClients(clientList);
      setMonteurs(monteurList);
      setAllWerkbonnen(werkbonnen);
      setAllOpnames(opnames);
      setAttentionThreads(Array.isArray(communicationRes?.data?.items) ? communicationRes.data.items : []);
      setCompletionRequirements(
        normalizeCompletionRequirements(settingsRes?.data?.completionRequirements)
      );
      setPendingTimeRequests(Array.isArray(timeRequestRes.data) ? timeRequestRes.data : []);
      setPendingTimeApprovals(
        werkbonnen.filter(
          (wb) =>
            wb?.timeMode === "auto" &&
            wb?.timeSubmissionStatus === "ingediend" &&
            wb?.timeApprovalStatus === "te_controleren"
        )
      );
    } catch (err) {
      setProjectsError(err?.response?.data?.message || "Projecten laden mislukt");
      setApprovalsError(err?.response?.data?.message || "Uren-goedkeuring laden mislukt");
      setTimeRequestsError(err?.response?.data?.message || "Extra tijd verzoeken laden mislukt");
      setHoursOverviewError(err?.response?.data?.message || "Urenoverzicht laden mislukt");
    } finally {
      setLoadingProjects(false);
    }
  }

  async function handleTimeRequestDecision(item, status) {
    if (!item?._id) return;

    try {
      setTimeRequestActionId(item._id);
      setTimeRequestsError("");
      await api.patch(`/time-extension-requests/${item._id}`, { status });
      await loadProjectData();
    } catch (err) {
      setTimeRequestsError(
        err?.response?.data?.message || "Extra tijd verzoek verwerken mislukt"
      );
    } finally {
      setTimeRequestActionId("");
    }
  }

  async function loadActionPolicies() {
    try {
      setPolicyLoading(true);
      setPolicyError("");
      setPolicySuccess("");

      const res = await api.get("/tenant/settings");
      const data = res?.data || {};
      setActionPolicy({
        allowMonteurCreate: Boolean(data.allowMonteurCreate),
        allowMonteurEditOwn: Boolean(data.allowMonteurEditOwn),
        allowMonteurCloseOwn: Boolean(data.allowMonteurCloseOwn),
      });
      setCompletionRequirements(
        normalizeCompletionRequirements(data.completionRequirements)
      );
      setIntegrationSettings(normalizeIntegrationSettings(data.integrations));
    } catch (err) {
      setPolicyError(
        err?.response?.data?.message || "Actie-policy's laden mislukt"
      );
    } finally {
      setPolicyLoading(false);
    }
  }

  async function updateActionPolicy(key, value) {
    const previous = { ...actionPolicy };
    setActionPolicy((prev) => ({ ...prev, [key]: value }));
    setPolicySavingKey(key);
    setPolicyError("");
    setPolicySuccess("");

    try {
      await api.put("/tenant/settings/action-policies", { [key]: value });
      setPolicySuccess("Actie-policy bijgewerkt.");
    } catch (err) {
      setActionPolicy(previous);
      setPolicyError(
        err?.response?.data?.message || "Actie-policy opslaan mislukt"
      );
    } finally {
      setPolicySavingKey("");
    }
  }

  async function updateIntegrationSetting(moduleKey, field, value) {
    const previous = integrationSettings;
    const next = {
      ...integrationSettings,
      [moduleKey]: {
        ...(integrationSettings[moduleKey] || {}),
        [field]: value,
      },
    };

    setIntegrationSettings(next);
    setPolicySavingKey(`${moduleKey}.${field}`);
    setPolicyError("");
    setPolicySuccess("");

    try {
      const payload = {
        [moduleKey]: {
          [field]: value,
        },
      };
      const res = await api.put("/tenant/settings/integrations", payload);
      const saved = normalizeIntegrationSettings(res?.data?.integrations);
      setIntegrationSettings(saved);
      setPolicySuccess("Integratie-instellingen bijgewerkt.");
    } catch (err) {
      setIntegrationSettings(previous);
      setPolicyError(
        err?.response?.data?.message || "Integratie-instellingen opslaan mislukt"
      );
    } finally {
      setPolicySavingKey("");
    }
  }

  function openNieuweWerkbon(project) {
    setSelectedProject(project);
    setShowNieuweWerkbon(true);
  }

  return (
    <>
      <section className="mb-4 flex items-center justify-end gap-2">
        <span className="text-xs text-slate-500">Kaartweergave</span>
        <button
          type="button"
          onClick={() => setCardDensity("compact")}
          className={`rounded-md border px-2.5 py-1 text-xs transition ${
            cardDensity === "compact"
              ? "border-slate-900 bg-slate-900 text-white"
              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          Compact
        </button>
        <button
          type="button"
          onClick={() => setCardDensity("comfort")}
          className={`rounded-md border px-2.5 py-1 text-xs transition ${
            cardDensity === "comfort"
              ? "border-slate-900 bg-slate-900 text-white"
              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          Comfort
        </button>
      </section>

      <div
        className={`grid grid-cols-1 md:grid-cols-2 ${
          cardDensity === "comfort" ? "gap-5 xl:grid-cols-3" : "gap-4 xl:grid-cols-4"
        }`}
      >
        <DashboardCard
          title="Open Werkbonnen"
          value={String(openWorkordersCount)}
          description="Nog uit te voeren"
          onClick={() => navigate("/werkbon")}
          density={cardDensity}
        />

        <DashboardCard
          title="Projecten Actief"
          value={String(projects.length)}
          description="Lopend"
          onClick={() => navigate("/pl/projecten")}
          density={cardDensity}
        />

        <DashboardCard
          title="Uren te keuren"
          value={String(pendingTimeApprovals.length)}
          description="Auto-uren ingediend"
          alert
          onClick={() => navigate("/pl/uren?focus=time-approval")}
          density={cardDensity}
        />

        <DashboardCard
          title="Uren Deze Maand"
          value={String(hoursThisMonth)}
          description="Geboekt"
          onClick={() => navigate("/pl/uren?period=month")}
          density={cardDensity}
        />

        <DashboardCard
          title="Meerwerk Open"
          value="EUR 4.250"
          description="Nog te factureren"
          onClick={() => navigate("/pl/projectcontrol")}
          density={cardDensity}
        />

        <DashboardCard
          title="Opnames totaal"
          value={String(opnameSummary.total)}
          description="Monteur opnames"
          onClick={() => navigate("/opname")}
          density={cardDensity}
        />

        <DashboardCard
          title="Opnames werkbon"
          value={String(opnameSummary.voorWerkbon)}
          description="Voor bestaande werkbon"
          onClick={() => navigate("/opname")}
          density={cardDensity}
        />

        <DashboardCard
          title="Opnames offerte"
          value={String(opnameSummary.voorOfferte)}
          description="Voor offerte / voorbereiding"
          onClick={() => navigate("/opname")}
          density={cardDensity}
        />

        <DashboardCard
          title="Opnames aandacht"
          value={String(opnameSummary.attention)}
          description="Nog op te volgen"
          alert={opnameSummary.attention > 0}
          onClick={() => navigate("/opname")}
          density={cardDensity}
        />

        <DashboardCard
          title="Nieuwe klantreacties"
          value={String(attentionThreads.length)}
          description={`${projectsWithCustomerReplies} projecten met reactie`}
          alert={attentionThreads.length > 0}
          onClick={() => navigate("/pl/projecten")}
          density={cardDensity}
        />

        <DashboardCard
          title="Niet gepland"
          value={String(actionRequiredSummary.unplanned)}
          description="Werkbonnen zonder datum"
          alert={actionRequiredSummary.unplanned > 0}
          onClick={() => navigate("/werkbon?attention=unplanned")}
          density={cardDensity}
        />

        <DashboardCard
          title="Geblokkeerd"
          value={String(actionRequiredSummary.blocked)}
          description="Direct aandacht nodig"
          alert={actionRequiredSummary.blocked > 0}
          onClick={() => navigate("/werkbon?attention=blocked")}
          density={cardDensity}
        />

        <DashboardCard
          title="Geen monteur"
          value={String(actionRequiredSummary.unassigned)}
          description="Nog niet gekoppeld"
          alert={actionRequiredSummary.unassigned > 0}
          onClick={() => navigate("/werkbon?attention=unassigned")}
          density={cardDensity}
        />

        <DashboardCard
          title="Admin incompleet"
          value={String(actionRequiredSummary.adminIncomplete)}
          description="Afgerond maar niet compleet"
          alert={actionRequiredSummary.adminIncomplete > 0}
          onClick={() => navigate("/werkbon?attention=admin-incomplete")}
          density={cardDensity}
        />

        <section className="bg-white rounded-xl shadow p-5 border-l-4 border-rose-500 md:col-span-2 xl:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold">Actie vereist</h3>
              <p className="text-sm text-gray-500">
                Werkbonnen die aandacht vragen op planning, uitvoering of administratie.
              </p>
            </div>
            <button
              onClick={loadProjectData}
              className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
            >
              Vernieuwen
            </button>
          </div>

          {loadingProjects && <p className="text-gray-500 text-sm">Laden...</p>}
          {projectsError && <p className="text-red-600 text-sm">{projectsError}</p>}

          {!loadingProjects && !projectsError && actionRequiredItems.length === 0 && (
            <p className="text-gray-500 text-sm">Geen directe actiepunten gevonden.</p>
          )}

          {!loadingProjects && actionRequiredItems.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-3">Werkbon</th>
                    <th className="py-2 pr-3">Omschrijving</th>
                    <th className="py-2 pr-3">Klant / project</th>
                    <th className="py-2 pr-3">Monteur</th>
                    <th className="py-2 pr-3">Aandachtspunt</th>
                    <th className="py-2 pr-3">Gepland</th>
                    <th className="py-2 text-right">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {actionRequiredItems.slice(0, 20).map((item) => (
                    <tr key={item.id} className="border-b align-top">
                      <td className="py-2 pr-3">{item.workOrderNumber}</td>
                      <td className="py-2 pr-3">{item.description}</td>
                      <td className="py-2 pr-3">
                        <div>{item.clientName}</div>
                        <div className="text-xs text-gray-500">{item.projectLabel}</div>
                      </td>
                      <td className="py-2 pr-3">{item.assignedLabel}</td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap gap-1">
                          {item.reasons.map((reason) => (
                            <span
                              key={reason.key}
                              className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getActionReasonClassName(
                                reason.key
                              )}`}
                            >
                              {reason.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 pr-3">{item.plannedLabel}</td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => {
                            window.location.href = `/werkbon/${item.id}`;
                          }}
                          className="bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded text-xs"
                        >
                          Open werkbon
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {isTenantAdmin && (
          <>
            <section className="bg-white rounded-xl shadow p-5 border-l-4 border-cyan-500 md:col-span-2 xl:col-span-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Actie-policy's</h3>
                <button
                  onClick={loadActionPolicies}
                  className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
                >
                  Vernieuwen
                </button>
              </div>

              {policyLoading && <p className="text-sm text-gray-500">Laden...</p>}
              {policyError && <p className="text-sm text-red-600">{policyError}</p>}
              {policySuccess && <p className="text-sm text-green-700">{policySuccess}</p>}

              {!policyLoading && (
                <div className="grid gap-3 md:grid-cols-3">
                  <PolicyToggle
                    label="Monteur mag acties maken"
                    description="Sta toe dat monteurs nieuwe acties toevoegen op werkbonnen."
                    checked={actionPolicy.allowMonteurCreate}
                    disabled={policySavingKey === "allowMonteurCreate"}
                    onChange={(value) => updateActionPolicy("allowMonteurCreate", value)}
                  />
                  <PolicyToggle
                    label="Monteur mag eigen acties bewerken"
                    description="Sta toe dat monteurs alleen hun eigen acties aanpassen."
                    checked={actionPolicy.allowMonteurEditOwn}
                    disabled={policySavingKey === "allowMonteurEditOwn"}
                    onChange={(value) => updateActionPolicy("allowMonteurEditOwn", value)}
                  />
                  <PolicyToggle
                    label="Monteur mag eigen acties sluiten"
                    description="Sta toe dat monteurs alleen hun eigen acties sluiten."
                    checked={actionPolicy.allowMonteurCloseOwn}
                    disabled={policySavingKey === "allowMonteurCloseOwn"}
                    onChange={(value) => updateActionPolicy("allowMonteurCloseOwn", value)}
                  />
                </div>
              )}
            </section>

            <section className="bg-white rounded-xl shadow p-5 border-l-4 border-emerald-500 md:col-span-2 xl:col-span-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Integraties</h3>
                <button
                  onClick={loadActionPolicies}
                  className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
                >
                  Vernieuwen
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <PolicyToggle
                  label="Moneybird module actief"
                  description="Toon Moneybird-flow in werkbon/facturatie."
                  checked={integrationSettings.moneybird.enabled}
                  disabled={policySavingKey === "moneybird.enabled"}
                  onChange={(value) =>
                    updateIntegrationSetting("moneybird", "enabled", value)
                  }
                />
                <PolicyToggle
                  label="Moneybird verbonden"
                  description="Gebruik dit pas na succesvolle OAuth/test."
                  checked={integrationSettings.moneybird.connected}
                  disabled={policySavingKey === "moneybird.connected"}
                  onChange={(value) =>
                    updateIntegrationSetting("moneybird", "connected", value)
                  }
                />
                <PolicyToggle
                  label="Moneybird auto-sync factuur"
                  description="Sync direct bij factureren vanuit werkbon."
                  checked={integrationSettings.moneybird.autoSyncOnInvoice}
                  disabled={policySavingKey === "moneybird.autoSyncOnInvoice"}
                  onChange={(value) =>
                    updateIntegrationSetting("moneybird", "autoSyncOnInvoice", value)
                  }
                />
              </div>

              <div className="grid gap-3 md:grid-cols-2 mt-3">
                <PolicyToggle
                  label="Technische Unie module actief"
                  description="Toon TU bestel/punchout flow in materiaalstroom."
                  checked={integrationSettings.tu.enabled}
                  disabled={policySavingKey === "tu.enabled"}
                  onChange={(value) =>
                    updateIntegrationSetting("tu", "enabled", value)
                  }
                />
                <PolicyToggle
                  label="Technische Unie verbonden"
                  description={
                    integrationSettings.tu.hasPassword
                      ? "Subaccount en credentials zijn aanwezig."
                      : "Zet pas aan nadat TU credentials zijn ingevoerd."
                  }
                  checked={integrationSettings.tu.connected}
                  disabled={policySavingKey === "tu.connected"}
                  onChange={(value) =>
                    updateIntegrationSetting("tu", "connected", value)
                  }
                />
              </div>
            </section>
          </>
        )}

        <section className="bg-white rounded-xl shadow p-5 border-l-4 border-blue-500 md:col-span-2 xl:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold">Nieuwe klantreacties</h3>
              <p className="text-sm text-gray-500">
                Portalreacties die nog interne aandacht vragen.
              </p>
            </div>
            <button
              onClick={loadProjectData}
              className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
            >
              Vernieuwen
            </button>
          </div>

          {loadingProjects && <p className="text-gray-500 text-sm">Laden...</p>}
          {projectsError && <p className="text-red-600 text-sm">{projectsError}</p>}

          {!loadingProjects && attentionThreads.length === 0 && (
            <p className="text-gray-500 text-sm">Geen nieuwe klantreacties.</p>
          )}

          {!loadingProjects && attentionThreads.length > 0 && (
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-3">Project</th>
                    <th className="py-2 pr-3">Onderwerp</th>
                    <th className="py-2 pr-3">Klant</th>
                    <th className="py-2 pr-3">Laatste klantreactie</th>
                    <th className="py-2 text-right">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {attentionThreads.slice(0, 10).map((thread) => {
                    const projectLabel =
                      thread?.projectId?.projectNummer ||
                      thread?.projectId?.number ||
                      thread?.projectId?.naam ||
                      thread?.projectId?.title ||
                      "-";
                    return (
                      <tr key={String(thread?._id || "")} className="border-b">
                        <td className="py-2 pr-3">{projectLabel}</td>
                        <td className="py-2 pr-3">{thread?.subject || "Gesprek"}</td>
                        <td className="py-2 pr-3">{thread?.clientId?.naam || "-"}</td>
                        <td className="py-2 pr-3">
                          {formatDateTime(thread?.lastCustomerReplyAt || thread?.lastMessageAt)}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => navigate("/pl/projecten")}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs"
                          >
                            Open project
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl shadow p-5 border-l-4 border-blue-500 md:col-span-2 xl:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">Actieve Projecten</h3>
            <button
              onClick={loadProjectData}
              className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
            >
              Vernieuwen
            </button>
          </div>

          {loadingProjects && <p className="text-gray-500 text-sm">Laden...</p>}
          {projectsError && <p className="text-red-600 text-sm">{projectsError}</p>}

          {!loadingProjects && !projectsError && projects.length === 0 && (
            <p className="text-gray-500 text-sm">Nog geen projecten gevonden.</p>
          )}

          {!loadingProjects && projects.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-3">Projectnr</th>
                    <th className="py-2 pr-3">Naam</th>
                    <th className="py-2 pr-3">Klant</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 text-right">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.slice(0, 10).map((p) => (
                    <tr key={p._id} className="border-b">
                      <td className="py-2 pr-3">{p.projectNummer || p.number || "-"}</td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{p.naam || p.title || "-"}</span>
                          {Number(communicationAttentionByProjectId[String(p._id || "")] || 0) > 0 ? (
                            <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                              {Number(communicationAttentionByProjectId[String(p._id || "")] || 0)} klantreact{Number(communicationAttentionByProjectId[String(p._id || "")] || 0) === 1 ? "ie" : "ies"}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        {clients.find(
                          (c) => String(c._id) === String(p.clientId?._id || p.clientId || "")
                        )?.naam || "-"}
                      </td>
                      <td className="py-2 pr-3">{p.status || "-"}</td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => openNieuweWerkbon(p)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs"
                        >
                          Werkbon aanmaken
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <PlannerTimeRequestPanel
          items={pendingTimeRequests}
          loading={loadingProjects}
          error={timeRequestsError}
          actingId={timeRequestActionId}
          onRefresh={loadProjectData}
          onApprove={(item) => handleTimeRequestDecision(item, "approved")}
          onReject={(item) => handleTimeRequestDecision(item, "rejected")}
        />

        <section className="bg-white rounded-xl shadow p-5 border-l-4 border-amber-500 md:col-span-2 xl:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">Uren Goedkeuring</h3>
            <button
              onClick={loadProjectData}
              className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
            >
              Vernieuwen
            </button>
          </div>

          {loadingProjects && <p className="text-gray-500 text-sm">Laden...</p>}
          {approvalsError && <p className="text-red-600 text-sm">{approvalsError}</p>}

          {!loadingProjects && !approvalsError && pendingTimeApprovals.length === 0 && (
            <p className="text-gray-500 text-sm">Geen uren die op goedkeuring wachten.</p>
          )}

          {!loadingProjects && pendingTimeApprovals.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-3">Werkbon</th>
                    <th className="py-2 pr-3">Omschrijving</th>
                    <th className="py-2 pr-3">Project</th>
                    <th className="py-2 pr-3">Ingediend</th>
                    <th className="py-2 text-right">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTimeApprovals.slice(0, 10).map((wb) => (
                    <tr key={wb._id} className="border-b">
                      <td className="py-2 pr-3">{wb.werkbonNummer || wb.nummer || "-"}</td>
                      <td className="py-2 pr-3">{getWerkbonOmschrijving(wb)}</td>
                      <td className="py-2 pr-3">
                        {wb.projectId?.projectNummer || wb.projectId?.naam || "-"}
                      </td>
                      <td className="py-2 pr-3">{formatDateTime(wb.timeApprovalSubmittedAt)}</td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => {
                            window.location.href = `/werkbon/${wb._id}`;
                          }}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded text-xs"
                        >
                          Open voor goedkeuring
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl shadow p-5 border-l-4 border-indigo-500 md:col-span-2 xl:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-lg font-semibold">Urenoverzicht Monteurs per Project</h3>
            <div className="flex flex-wrap gap-2">
              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                className="text-sm border rounded px-3 py-1"
              >
                <option value="week">Deze week</option>
                <option value="month">Deze maand</option>
                <option value="all">Alles</option>
              </select>
              <select
                value={selectedProjectFilter}
                onChange={(e) => setSelectedProjectFilter(e.target.value)}
                className="text-sm border rounded px-3 py-1 min-w-[220px]"
              >
                <option value="all">Alle projecten</option>
                {projectFilterOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                onClick={loadProjectData}
                className="text-sm border rounded px-3 py-1 hover:bg-gray-50"
              >
                Vernieuwen
              </button>
            </div>
          </div>

          {loadingProjects && <p className="text-gray-500 text-sm">Laden...</p>}
          {hoursOverviewError && <p className="text-red-600 text-sm">{hoursOverviewError}</p>}

          {!loadingProjects && !hoursOverviewError && filteredTechnicianProjectHours.length === 0 && (
            <p className="text-gray-500 text-sm">Geen uren gevonden voor deze filters.</p>
          )}

          {!loadingProjects && filteredTechnicianProjectHours.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-3">Monteur</th>
                    <th className="py-2 pr-3">Project</th>
                    <th className="py-2 pr-3">Werkbonnen</th>
                    <th className="py-2 pr-3 text-right">Uren totaal</th>
                    <th className="py-2 text-right">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTechnicianProjectHours.slice(0, 25).map((row) => {
                    const rowKey = getHoursRowKey(row);
                    const selected = rowKey === selectedHoursRowKey;
                    return (
                      <tr
                        key={rowKey}
                        onClick={() => setSelectedHoursRowKey(rowKey)}
                        className={`border-b cursor-pointer ${selected ? "bg-indigo-50" : "hover:bg-gray-50"}`}
                      >
                        <td className="py-2 pr-3">{row.technicianName}</td>
                        <td className="py-2 pr-3">{row.projectLabel}</td>
                        <td className="py-2 pr-3">{row.workOrderCount}</td>
                        <td className="py-2 pr-3 text-right">{toHours(row.totalHours)}</td>
                        <td className="py-2 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedHoursRowKey(rowKey);
                            }}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded text-xs"
                          >
                            Toon werkbonnen
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {selectedHoursRow && (
            <div className="mt-4 rounded border bg-gray-50 p-3">
              <h4 className="font-semibold mb-2">
                Relevante werkbonnen: {selectedHoursRow.technicianName} / {selectedHoursRow.projectLabel}
              </h4>
              {relatedWorkOrders.length === 0 && (
                <p className="text-sm text-gray-500">Geen werkbonnen gevonden voor deze selectie.</p>
              )}
              {relatedWorkOrders.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-2 pr-3">Werkbon</th>
                        <th className="py-2 pr-3">Omschrijving</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Project</th>
                        <th className="py-2 text-right">Open</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relatedWorkOrders.map((wb) => (
                        <tr key={wb._id} className="border-b">
                          <td className="py-2 pr-3">{wb.werkbonNummer || wb.nummer || "-"}</td>
                          <td className="py-2 pr-3">{getWerkbonOmschrijving(wb)}</td>
                          <td className="py-2 pr-3">{wb.status || "-"}</td>
                          <td className="py-2 pr-3">
                            {wb.projectId?.projectNummer || wb.projectNummer || "-"}
                          </td>
                          <td className="py-2 text-right">
                            <button
                              onClick={() => {
                                window.location.href = `/werkbon/${wb._id}`;
                              }}
                              className="bg-gray-800 hover:bg-gray-900 text-white px-3 py-1.5 rounded text-xs"
                            >
                              Open werkbon
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {showNieuweWerkbon && selectedProject && (
        <NieuweWerkbonModal
          initialProjectId={selectedProject._id}
          initialClientId={String(selectedProject.clientId?._id || selectedProject.clientId || "")}
          onClose={() => {
            setShowNieuweWerkbon(false);
            setSelectedProject(null);
          }}
          onCreated={(wb) => {
            setShowNieuweWerkbon(false);
            setSelectedProject(null);
            if (wb?._id) {
              window.location.href = `/werkbon/${wb._id}`;
            }
          }}
        />
      )}
    </>
  );
}

function asArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  return [];
}

function idFrom(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value._id) return String(value._id);
  return "";
}

function getProjectLabel(wb, projectLookup) {
  const rawProjectId = idFrom(wb?.projectId);
  const fromLookup = rawProjectId ? projectLookup.get(rawProjectId) : "";
  return fromLookup || wb?.projectNummer || wb?.opdrachtNummer || "-";
}

function toValidDate(...values) {
  for (const value of values) {
    if (!value) continue;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return null;
}

function startOfWeek(date) {
  const start = new Date(date);
  const day = (start.getDay() + 6) % 7;
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - day);
  return start;
}

function isInPeriod(dateInput, period) {
  if (period === "all") return true;
  const date = toValidDate(dateInput);
  if (!date) return false;

  const now = new Date();
  if (period === "month") {
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  }

  if (period === "week") {
    const start = startOfWeek(now);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return date >= start && date < end;
  }

  return true;
}

function getHoursRowKey(row) {
  return `${row.technicianId}::${row.projectKey}`;
}

function buildTechnicianProjectHours(werkbonnen, projects, monteurs, periodFilter = "all") {
  const projectLookup = new Map(
    (projects || []).map((p) => [
      String(p._id),
      p.projectNummer || p.number || p.naam || p.title || "-",
    ])
  );
  const technicianLookup = new Map(
    (monteurs || []).map((u) => [String(u._id), u.naam || u.name || u.email || String(u._id)])
  );
  const grouped = new Map();

  function addRow(technicianId, projectKey, projectLabel, workOrderId, hours) {
    const safeHours = Number(hours || 0);
    if (!Number.isFinite(safeHours) || safeHours <= 0) return;

    const safeTechnicianId = technicianId || "onbekend";
    const key = `${safeTechnicianId}::${projectKey}`;
    const existing = grouped.get(key) || {
      technicianId: safeTechnicianId,
      technicianName: technicianLookup.get(safeTechnicianId) || "Onbekend",
      projectKey,
      projectLabel: projectLabel || "-",
      totalHours: 0,
      workOrderIds: new Set(),
    };
    existing.totalHours += safeHours;
    if (workOrderId) existing.workOrderIds.add(String(workOrderId));
    grouped.set(key, existing);
  }

  for (const wb of werkbonnen || []) {
    const workOrderId = wb?._id;
    const projectId = idFrom(wb?.projectId);
    const projectKey = projectId || `project-unknown-${wb?.projectNummer || wb?.werkbonNummer || "x"}`;
    const projectLabel = getProjectLabel(wb, projectLookup);
    const fallbackTechnicianId =
      idFrom(wb?.assignedTo) || idFrom(wb?.team?.[0]?.userId) || idFrom(wb?.createdBy);

    for (const entry of wb?.hourEntries || []) {
      const entryDate = toValidDate(entry?.datum, wb?.createdAt);
      if (!isInPeriod(entryDate, periodFilter)) continue;
      const technicianId = idFrom(entry?.technicianId) || fallbackTechnicianId;
      addRow(technicianId, projectKey, projectLabel, workOrderId, entry?.hours);
    }

    for (const entry of wb?.autoTimeBaseEntries || []) {
      const entryDate = toValidDate(entry?.datum, entry?.startAt, entry?.endAt, wb?.createdAt);
      if (!isInPeriod(entryDate, periodFilter)) continue;
      addRow(fallbackTechnicianId, projectKey, projectLabel, workOrderId, entry?.hours);
    }

    for (const entry of wb?.autoAdjustments || []) {
      const entryDate = toValidDate(entry?.datum, wb?.createdAt);
      if (!isInPeriod(entryDate, periodFilter)) continue;
      const technicianId = idFrom(entry?.createdBy) || fallbackTechnicianId;
      addRow(technicianId, projectKey, projectLabel, workOrderId, entry?.hours);
    }
  }

  return Array.from(grouped.values())
    .map((row) => ({
      technicianId: row.technicianId,
      technicianName: row.technicianName,
      projectKey: row.projectKey,
      projectLabel: row.projectLabel,
      totalHours: row.totalHours,
      workOrderCount: row.workOrderIds.size,
      workOrderIds: Array.from(row.workOrderIds),
    }))
    .sort(
      (a, b) =>
        a.technicianName.localeCompare(b.technicianName, "nl-NL") ||
        b.totalHours - a.totalHours
    );
}

function normalizeProjectNummer(value) {
  return String(value || "").trim().toLowerCase();
}

function isOpnameVoorWerkbon(opname, werkbonIdSet, projectNummerSet) {
  const linkedWerkbonId = String(opname?.gekoppeldeWerkbonId || "").trim();
  if (linkedWerkbonId && werkbonIdSet.has(linkedWerkbonId)) return true;

  const projectNummer = normalizeProjectNummer(opname?.projectNummer);
  if (projectNummer && projectNummerSet.has(projectNummer)) return true;

  return false;
}

function summarizeOpnames(opnames, werkbonnen) {
  const safeOpnames = Array.isArray(opnames) ? opnames : [];
  const safeWerkbonnen = Array.isArray(werkbonnen) ? werkbonnen : [];

  const werkbonIdSet = new Set(
    safeWerkbonnen
      .map((wb) => String(wb?._id || "").trim())
      .filter(Boolean)
  );

  const projectNummerSet = new Set(
    safeWerkbonnen
      .map((wb) => normalizeProjectNummer(wb?.projectNummer || wb?.projectId?.projectNummer || ""))
      .filter(Boolean)
  );

  let voorWerkbon = 0;
  let attention = 0;
  for (const opname of safeOpnames) {
    if (isOpnameVoorWerkbon(opname, werkbonIdSet, projectNummerSet)) {
      voorWerkbon += 1;
    }
    const status = String(opname?.status || "concept").trim().toLowerCase();
    const isClosed = ["opgevolgd", "verwerkt", "afgerond"].includes(status);
    if (!isClosed) {
      attention += 1;
    }
  }

  return {
    total: safeOpnames.length,
    voorWerkbon,
    voorOfferte: Math.max(0, safeOpnames.length - voorWerkbon),
    attention,
  };
}

function buildActionRequiredItems(
  werkbonnen,
  projects,
  clients,
  monteurs,
  completionRequirements
) {
  const projectLookup = new Map(
    (projects || []).map((project) => [
      String(project._id),
      {
        label: project.projectNummer || project.number || project.naam || project.title || "-",
        clientId: idFrom(project.clientId),
      },
    ])
  );
  const clientLookup = new Map(
    (clients || []).map((client) => [
      String(client._id),
      client.naam || client.name || client.bedrijfsnaam || "-",
    ])
  );
  const monteurLookup = new Map(
    (monteurs || []).map((user) => [
      String(user._id),
      user.naam || user.name || user.email || "Onbekend",
    ])
  );

  return (werkbonnen || [])
    .map((wb) => {
      const statusKey = normalizeWerkbonStatus(wb?.status);
      const reasons = [];
      const teamIds = getAssignedUserIds(wb);
      const photoCount = getWerkbonPhotoCount(wb);
      const hasHours = getTotalHours(wb) > 0;
      const hasBlockedDailyLog = Array.isArray(wb?.dailyLogs)
        ? wb.dailyLogs.some((entry) => entry?.blocked)
        : false;
      const adminCompletion = getAdministrativeCompletionState(wb, completionRequirements);
      const isBlocked =
        statusKey === "geblokkeerd" ||
        Boolean(String(wb?.blockedReasonCode || "").trim()) ||
        Boolean(String(wb?.blockedReasonText || "").trim()) ||
        hasBlockedDailyLog;

      if (!["afgerond"].includes(statusKey) && !wb?.plannedDate) {
        reasons.push({ key: "unplanned", label: "Niet gepland", priority: 80 });
      }

      if (!["afgerond"].includes(statusKey) && teamIds.length === 0) {
        reasons.push({ key: "unassigned", label: "Geen monteur", priority: 75 });
      }

      if (isBlocked) {
        reasons.push({ key: "blocked", label: "Geblokkeerd", priority: 100 });
      }

      if (
        wb?.timeMode === "auto" &&
        wb?.timeSubmissionStatus === "ingediend" &&
        wb?.timeApprovalStatus === "te_controleren"
      ) {
        reasons.push({ key: "timeApproval", label: "Uren te keuren", priority: 90 });
      }

      if (statusKey === "in_uitvoering" && !hasHours) {
        reasons.push({ key: "missingHours", label: "Geen uren geboekt", priority: 70 });
      }

      if (statusKey === "in_uitvoering" && photoCount === 0) {
        reasons.push({ key: "missingPhotos", label: "Geen foto's", priority: 55 });
      }

      if (statusKey === "afgerond") {
        for (const missing of adminCompletion.missing) {
          const label = getAdminIncompleteLabel(missing);
          if (label) {
            reasons.push({
              key: "adminIncomplete",
              label,
              priority: 85,
            });
          }
        }
      }

      if (!reasons.length) return null;

      const projectId = idFrom(wb?.projectId);
      const projectMeta = projectLookup.get(projectId) || null;
      const clientName =
        clientLookup.get(idFrom(wb?.clientId)) ||
        clientLookup.get(projectMeta?.clientId) ||
        "-";
      const assignedLabel =
        teamIds.map((id) => monteurLookup.get(id) || "Onbekend").join(", ") || "-";

      return {
        id: String(wb?._id || ""),
        sortPriority: Math.max(...reasons.map((reason) => reason.priority)),
        updatedAt: toValidDate(wb?.updatedAt, wb?.createdAt)?.getTime() || 0,
        workOrderNumber: wb?.werkbonNummer || wb?.nummer || "-",
        description: getWerkbonOmschrijving(wb),
        projectLabel: projectMeta?.label || getProjectLabel(wb, new Map()),
        clientName,
        assignedLabel,
        plannedLabel: formatDateTime(wb?.plannedDate),
        reasons: dedupeActionReasons(reasons),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.sortPriority - a.sortPriority || b.updatedAt - a.updatedAt);
}

function dedupeActionReasons(reasons) {
  const seen = new Set();
  const result = [];
  for (const reason of reasons) {
    const key = `${reason.key}:${reason.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(reason);
  }
  return result;
}

function summarizeActionRequired(items) {
  return {
    unplanned: countActionReason(items, "unplanned"),
    blocked: countActionReason(items, "blocked"),
    unassigned: countActionReason(items, "unassigned"),
    adminIncomplete: countActionReason(items, "adminIncomplete"),
  };
}

function countActionReason(items, reasonKey) {
  return (items || []).filter((item) => item.reasons.some((reason) => reason.key === reasonKey))
    .length;
}

function normalizeWerkbonStatus(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "gereed voor planning") return "gereed_voor_planning";
  if (raw === "in uitvoering") return "in_uitvoering";
  return raw.replace(/\s+/g, "_");
}

function getAssignedUserIds(wb) {
  const ids = new Set();
  const assignedToId = idFrom(wb?.assignedTo);
  if (assignedToId) ids.add(assignedToId);
  for (const member of wb?.team || []) {
    const id = idFrom(member?.userId);
    if (id) ids.add(id);
  }
  return Array.from(ids);
}

function getTotalHours(wb) {
  let total = 0;
  for (const entry of wb?.hourEntries || []) {
    total += Number(entry?.hours || 0);
  }
  for (const entry of wb?.autoTimeBaseEntries || []) {
    total += Number(entry?.hours || 0);
  }
  for (const entry of wb?.autoAdjustments || []) {
    total += Number(entry?.hours || 0);
  }
  return total;
}

function getWerkbonPhotoCount(wb) {
  const photoCollections = [wb?.werkbonFotos, wb?.fotos, wb?.photos];
  return photoCollections.reduce((count, collection) => {
    if (Array.isArray(collection)) return count + collection.length;
    return count;
  }, 0);
}

function getMaterialCount(wb) {
  let count = 0;
  for (const entry of wb?.materialEntries || []) {
    if (Number(entry?.qty || 0) > 0) count += 1;
  }
  for (const entry of wb?.materialen || []) {
    if (Number(entry?.aantal || 0) > 0) count += 1;
  }
  return count;
}

function hasExecutionLogClient(wb) {
  const hasDailyLogs = (wb?.dailyLogs || []).some(
    (entry) => String(entry?.description || "").trim() || Number(entry?.uren || 0) > 0
  );
  if (hasDailyLogs) return true;
  return (wb?.daglog || []).some((entry) => String(entry?.omschrijving || "").trim());
}

function getAdministrativeCompletionState(wb, completionRequirements) {
  const completionChecks = wb?.completionChecks || {};
  const requirements = normalizeCompletionRequirements(completionRequirements);
  const missing = [];
  const hours = getTotalHours(wb);
  const materialCount = getMaterialCount(wb);
  const evidenceCount =
    getWerkbonPhotoCount(wb) + (Array.isArray(wb?.bijlagen) ? wb.bijlagen.length : 0);

  if (requirements.requireExecutionLog && !hasExecutionLogClient(wb)) missing.push("executionLog");
  if (requirements.requireHours && hours <= 0 && completionChecks.zeroHoursConfirmed !== true) {
    missing.push("hours");
  }
  if (
    requirements.requireMaterials &&
    materialCount <= 0 &&
    completionChecks.noMaterialConfirmed !== true
  ) {
    missing.push("materials");
  }
  if (
    requirements.requireEvidence &&
    evidenceCount <= 0 &&
    completionChecks.noEvidenceConfirmed !== true
  ) {
    missing.push("evidence");
  }

  return {
    missing,
    isComplete: missing.length === 0,
  };
}

function getAdminIncompleteLabel(key) {
  if (key === "executionLog") return "Afronding mist daglog";
  if (key === "hours") return "Afronding mist uren";
  if (key === "materials") return "Afronding mist materiaal";
  if (key === "evidence") return "Afronding mist bewijs";
  return "";
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toHours(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
}

function getWerkbonOmschrijving(werkbon) {
  return (
    werkbon?.omschrijving ||
    werkbon?.opdracht?.omschrijving ||
    werkbon?.scope?.samenvatting ||
    "-"
  );
}

function getRoleNames(user) {
  const roleSet = new Set();
  if (user?.rol) roleSet.add(String(user.rol).trim().toLowerCase());
  if (user?.role) roleSet.add(String(user.role).trim().toLowerCase());
  for (const role of user?.roles || []) {
    if (!role) continue;
    if (typeof role === "string") {
      roleSet.add(role.trim().toLowerCase());
      continue;
    }
    if (typeof role === "object" && (role.name || role.naam)) {
      roleSet.add(String(role.name || role.naam).trim().toLowerCase());
    }
  }
  return Array.from(roleSet);
}

function normalizeIntegrationSettings(value) {
  const integrations = value || {};
  const moneybird = integrations.moneybird || {};
  const tu = integrations.tu || {};

  return {
    moneybird: {
      enabled: Boolean(moneybird.enabled),
      connected: Boolean(moneybird.connected),
      autoSyncOnInvoice:
        moneybird.autoSyncOnInvoice === undefined
          ? true
          : Boolean(moneybird.autoSyncOnInvoice),
    },
    tu: {
      enabled: Boolean(tu.enabled),
      connected: Boolean(tu.connected),
      hasPassword: Boolean(tu.hasPassword),
    },
  };
}

function getActionReasonClassName(reasonKey) {
  if (reasonKey === "blocked") return "bg-rose-100 text-rose-700";
  if (reasonKey === "timeApproval") return "bg-amber-100 text-amber-800";
  if (reasonKey === "adminIncomplete") return "bg-violet-100 text-violet-800";
  if (reasonKey === "unassigned") return "bg-sky-100 text-sky-800";
  if (reasonKey === "unplanned") return "bg-slate-100 text-slate-700";
  return "bg-orange-100 text-orange-800";
}

function normalizeCompletionRequirements(value) {
  const source = value || {};
  return {
    requireExecutionLog:
      source.requireExecutionLog === undefined ? true : Boolean(source.requireExecutionLog),
    requireHours: source.requireHours === undefined ? true : Boolean(source.requireHours),
    requireMaterials:
      source.requireMaterials === undefined ? false : Boolean(source.requireMaterials),
    requireEvidence:
      source.requireEvidence === undefined ? true : Boolean(source.requireEvidence),
  };
}

function PolicyToggle({ label, description, checked, onChange, disabled }) {
  return (
    <label className="rounded border p-3 flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-gray-500">{description}</span>
      </span>
    </label>
  );
}

function DashboardCard({ title, value, description, alert, onClick, density = "compact" }) {
  const Wrapper = onClick ? "button" : "div";
  const comfort = density === "comfort";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`bg-white rounded-lg shadow-sm border-l-4 ${
        comfort ? "p-4 min-h-[116px]" : "p-3 min-h-[96px]"
      } ${
        alert ? "border-red-500" : "border-blue-500"
      } ${onClick ? "text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-300" : ""}`}
    >
      <h3 className={`${comfort ? "text-sm" : "text-xs"} font-medium text-gray-500`}>{title}</h3>
      <div className={`mt-1 font-bold leading-tight ${comfort ? "text-2xl" : "text-xl"}`}>{value}</div>
      <div className={`${comfort ? "mt-2 text-xs" : "mt-1 text-[11px]"} text-gray-400`}>{description}</div>
    </Wrapper>
  );
}
